import type { MessageKey } from './ko.js';

/**
 * **`Record<MessageKey, string>` 이 번역 누락을 잡는 유일한 자동 검사다** (절대 규칙 24).
 * 키가 빠져도 잉여여도 `pnpm typecheck` 가 깨진다.
 *
 * **문장의 품질은 타입이 잡지 못한다.** 시청자가 읽는 화면이라 콘솔보다 더 중요하고,
 * 사람이 한 번 읽어야 한다 (`V9-확장-지시서.md` §8.2 — L6).
 */
export const en: Record<MessageKey, string> = {
  // ── Common ──────────────────────────────────────────────────────────
  'common.close': 'Close',
  'common.retry': 'Try again',
  'common.loading': 'Getting ready…',
  'common.watchBroadcast': 'Watch the channel',
  'common.unknownError': 'Unknown error',

  // ── Start gate ──────────────────────────────────────────────────────
  'gate.hint':
    'On shopping channels, product videos play back to back like one continuous channel. On drama channels, sign in to pick up where you left off.',
  'gate.start': 'Start watching',
  'gate.hintResume': 'Welcome back from the store. You will pick up where you left off.',
  'gate.startResume': 'Resume watching',
  'gate.openGuide': 'Browse the guide',

  // ── Home (v32) ───────────────────────────────────────────────────────
  'home.nav.guide': 'Guide',
  'home.rail.favorites': 'Favorites',
  'home.rail.channels': 'Channels',
  'home.rail.selling': 'On sale now',
  'home.startFromTop': 'Watch from the start',
  'home.shopFromTop': 'Watch this item from the start',
  'home.next': 'Next',
  'home.pressOk': 'Press OK to play with sound',
  'watch.home': 'Home',
  'settings.homePreview': 'Preview on Home',
  'settings.homePreviewOn': 'On',
  'settings.homePreviewOff': 'Off',

  // ── Watch screen ────────────────────────────────────────────────────
  'watch.loading': 'Loading…',
  'watch.settings': 'Settings',
  'watch.channelUp': 'Previous channel',
  'watch.channelDown': 'Next channel',
  'watch.mute': 'Mute',
  'watch.unmute': 'Unmute',
  'watch.buyOnMobile': 'Buy on your phone',
  'watch.buyHere': 'Buy now',
  'watch.requesting': 'Sending…',
  'watch.info': 'Details',
  'watch.skip': 'Skip',
  'watch.skipIn': 'Skip in {n}s',
  'watch.allProducts': 'All products',
  'watch.allEpisodes': 'All episodes',
  // v39 content selection (`docs/81` §5.1)
  'watch.allContents': 'Content',
  'contents.title': 'Content',
  'contents.hint': 'Pick a series to see its episodes. Series that are not scheduled can be watched on this channel too.',
  'contents.episodeCount': '{n} episodes',
  'contents.watching': 'Watching',
  'contents.scheduled': 'On schedule',
  'contents.library': 'Library',
  'contents.back': 'Content',
  'contents.loadFailed': 'Could not load the library. Only scheduled episodes are shown.',
  'contents.episodesFailed': 'Could not load the episodes.',

  // v28 ONAIR.
  'onair.live': 'On air',
  'onair.pending': 'Not on air yet',
  'onair.playbackFailed': 'This broadcast cannot be played',
  'onair.badge': 'TV',
  'epg.watching': 'Watching',
  'onair.past': 'Already aired',
  // v23. 상품 오버레이. **상품명·제휴사명은 여기 없다** — 외부 데이터다.
  'offer.badge': 'Spotted in this scene',
  'offer.close': 'Close',
  'offer.buy': 'Buy on your phone',
  'offer.buyHere': 'Buy now',
  'watch.nextEpisode': 'Next episode',
  'watch.resumeToast': 'Resuming {episode} from {time}',
  'watch.recordedLocked': 'You can watch other episodes once the live ad ends',
  'episodes.title': 'All episodes',
  'episodes.hint': 'The episode you pick starts from the beginning.',
  'episodes.label': 'S{season} · E{episode}',
  'episodes.labelNoSeason': 'E{episode}',
  'episodes.resumeAt': 'Resume from {time}',
  'episodes.duration': '{n} min',
  'watch.remaining': '{left} left / {total}',
  // ── Ads (v21) ─────────────────────────────────────────────────────
  'watch.adRemaining': 'Ad {left} left / {total}',
  'watch.adPurchase': 'See this ad’s product',
  // v33. 라이브 이벤트 — 배지 하나. 이벤트 제목은 사람이 넣은 값이라 번역되지 않는다.
  'watch.liveBadge': '🔴 LIVE',
  'watch.toLive': '🔴 Watch live',
  'watch.toRecorded': 'Resume replay',
  // v35. Recorded-content transport — absent during live and ads.
  'watch.back10': 'Back 10 seconds',
  'watch.forward10': 'Forward 10 seconds',
  'watch.pause': 'Pause',
  'watch.resume': 'Play',
  'epg.liveBadge': '🔴 LIVE',
  'epg.live.waiting': 'Waiting to start',
  'epg.live.extended': 'Running over',
  'epg.live.expectedEnd': 'Expected end {time}',
  'watch.adSkip': 'Skip ad',
  'watch.adSkipIn': 'Skip in {n}s',
  'watch.adBadge': 'Ad',
  'watch.adLoad': '+{n} min of ads',

  // ── Select and buy (v11) ────────────────────────────────────────────
  'products.title': 'All products',
  'products.hint': 'Pick a product to play it now, then buy it from the same screen.',
  'products.nowPlaying': 'Now playing',

  // ── Handoff overlays ────────────────────────────────────────────────
  'handoff.pushSent_one': 'Sent a notification to your phone',
  'handoff.pushSent_other': 'Sent a notification to {n} phones',
  'handoff.pushSentHint': 'Tap the notification on your phone to keep shopping.',
  'handoff.pushSentWithQrHint':
    "If your phone doesn't receive a notification, scan the QR code on the right.",
  'handoff.qrIfSilent': "If your phone doesn't receive a notification",
  'handoff.failed': 'Could not hand off to a phone',
  'handoff.failedQrHint': '{reason} · scan the QR code to buy',
  'qr.scanHint': 'Scan with your phone',
  'qr.alt': 'QR code for the purchase page',

  'handoff.matchedByAccount': 'Found through your account',
  'handoff.matchedByNetwork': 'Found on the same network',

  'handoff.reason.timeout': 'the server is slow to answer',
  'handoff.reason.network': 'we could not reach the server',
  'handoff.reason.status': 'the request was not processed',
  'handoff.reason.noPhone': 'no phone was found to notify',
  'handoff.reason.noQr': 'no QR code came back',
  'handoff.reason.notAvailable': 'hand-off is not available right now',
  'handoff.reason.unknown': 'an unknown error happened',

  'handoff.note.pushedOthers_one': 'We also notified one other phone',
  'handoff.note.pushedOthers_other': 'We also notified {n} other phones',
  'handoff.note.serverChoseQr': 'The server chose the QR route',
  'handoff.note.notRecorded': 'We could not record this purchase',

  // ── Channel list (v9) ───────────────────────────────────────────────
  'epg.title': 'Channel guide',
  'epg.hint': 'Playback starts from whatever you pick.',
  'epg.favorites': 'Favorite channels',
  'epg.allChannels': 'All channels',
  'epg.emptyFavorites': 'Star a channel name to pin it here.',
  'epg.favorite': 'Favorite',
  'epg.channelNumber': 'Channel {n}',
  'epg.entryPoint': 'Now',
  'epg.nowPlaying': 'Playing now',
  'epg.startsInAbout': 'in ~{n} min',
  'epg.signedOutHint': 'Sign in to resume drama channels where you left off.',
  'epg.scrollHint': 'Use \u2190 \u2192 to see more',
  'epg.gridHint': 'Time runs across, channels run down. Pick a box to start watching.',
  'epg.nowMarker': 'Now',
  'epg.backToNow': 'Back to now',
  'epg.adsLabel': 'Ad time',
  'epg.adsOn': 'On',
  'epg.adsOff': 'Off',
  'epg.timeAxis': 'Time axis',
  'epg.seenPart': 'Already watched',
  'epg.repeats': 'From the start again',
  'channels.kindHomeshopping': 'Shopping',
  'channels.genre.drama': 'Drama',
  'channels.genre.movie': 'Movies',
  'channels.genre.sports': 'Sports',
  'channels.genre.news': 'News',
  'channels.genre.entertainment': 'Entertainment',
  'channels.genre.documentary': 'Documentary',
  'channels.itemCount_one': '{n} product',
  'channels.itemCount_other': '{n} products',
  'channels.episodeCount_one': '{n} episode',
  'channels.episodeCount_other': '{n} episodes',
  'channels.emptyTitle': 'No channel is on air',
  'channels.emptyText':
    'No channel has anything playable scheduled yet. Finish scheduling in the admin console and open this again.',
  'channels.change': 'Switch channel',

  // ── Entry badges ────────────────────────────────────────────────────
  // **`bloom-server` 의 `EN_TITLES` 와 같은 문자열이어야 한다**
  // (`src/bloom/push/notification-copy.ts`). 그쪽이 영어 알림의 제목을 만든다.
  // 타입으로 묶지 못한다 — 앱이 서버를 import 하지 않는다. 아래 테스트 주석 참조.
  'badge.triggerAd': '📺 Seen on TV',
  'badge.triggerChannelContent': '📺 On air now',
  // v23. 짝은 `bloom-server` 의 `EN_TITLES.content_offer` 다 — **타입으로 묶지 못한다.**
  'badge.triggerContentOffer': '🛍 Spotted in the scene',
  'badge.tv2m': 'Opened from a notification',
  'badge.tv2mSameDevice': 'Continued on this device',
  'badge.tv2mDeferred': 'Restored after installing from QR',
  'badge.tv2mSameDeviceDeferred': 'Restored after installing',

  // ── Product screen and sheet ────────────────────────────────────────
  'product.title': 'Product',
  'product.loadingTitle': 'Loading product details',
  'product.continueWatching': 'Keep watching',
  'product.coupon': 'Coupon {code} applies',
  'product.loadFailed':
    'Could not load the product. It may not be on air yet, or the product server is unreachable.',
  'product.creativeLoadFailed':
    'Could not load the ad creative. The campaign may have ended, or the ad server is unreachable.',
  'product.noDeepLink': 'No store address is available for this product.',
  'product.goToStore': 'Go to the store',
  'product.leaving': 'Opening the store…',
  'product.openedInNewTab': 'The store opened in a new tab.',
  'product.handedOffToStore': 'Opening the store. You will pick up where you left off.',
  'product.purchaseDone': 'Purchase complete (demo)',
  'product.idLabel': 'Product id',
  'product.missingTitle': 'No product to show',
  'product.missingText':
    'The link expired, or no product is stored on this device. Go back to the channel and press [Buy on your phone] again.',

  // ── Settings sheet ──────────────────────────────────────────────────
  'settings.title': 'Settings',
  'settings.language': 'Language',
  'settings.ads': 'Ads',
  'settings.adsOn': 'On',
  'settings.adsOff': 'Off',
  'settings.adsHint':
    'For demos. Turning it off skips ads from the next break on; playback never stops.',
  // v23. **`offerPolicy !== null` 일 때만 렌더된다** (절대 규칙 66).
  'settings.offers': 'Product picks',
  'settings.offersOn': 'On',
  'settings.offersOff': 'Off',
  'settings.offersHint':
    'For demos. It applies to this device only, and a card already on screen stays.',
  'settings.preparing': 'Preparing',
  'settings.householdFailed': 'Household registration failed — the server was unreachable',
  'settings.householdOk': 'Registered to this household',
  'settings.resumeLastProduct': 'Resume the last product you viewed',
  'settings.openDiagnostics': 'Open diagnostics',
  'settings.notLoggedIn': 'Not signed in — matching falls back to your network (IP)',
  'settings.deviceLinked': 'This device is linked to your account',
  'settings.deviceNotLinked': 'Not linked to an account — account matching will not work',
  'settings.signOut': 'Sign out',
  'settings.signingOut': 'Signing out…',
  'settings.signInBenefit':
    'Sign in and notifications also reach your other devices on the same account — even on a different network.',

  // ── Notification permission ─────────────────────────────────────────
  'push.iosTitle': 'Add this to your home screen to get notifications',
  'push.iosText':
    'On iPhone and iPad, notifications work only after you add this to the home screen.',
  'push.iosStep1': 'Tap the share button (□↑) at the bottom of Safari.',
  'push.iosStep2': 'Choose [Add to Home Screen].',
  'push.iosStep3': 'Open it again from the home screen icon and turn notifications on.',
  'push.on': 'Notifications on',
  'push.enableTitle': 'Turn on notifications',
  'push.enableText':
    'When you pick a product on the TV, we notify this phone right away. Without notifications you have to scan the QR code again.',
  'push.enable': 'Turn on notifications',
  'push.requesting': 'Requesting…',
  'push.subscribed': 'Notifications are on.',
  'push.denied': 'Notifications are blocked. Allow them in your browser site settings.',
  'push.noVapid':
    'The server has no notification key (VAPID) configured. Let an administrator know.',
  'push.unsupported': 'This browser does not support web notifications.',
  'push.subscribeFailed': 'Could not create the notification subscription. Try again in a moment.',
  'push.keyMismatch':
    'The notification key no longer matches the server. Reopen the app to subscribe with the new key.',
  'push.modeMismatch':
    "This deployment's notification setup does not match the app. Let an administrator know.",
  'push.registerFailed':
    'Could not register the subscription with the server. Try again in a moment.',
  'push.notAsked': 'Notification permission has not been requested yet.',

  // ── Sign in ─────────────────────────────────────────────────────────
  'login.title': 'Sign in to start BLOOM',
  'login.text': 'A Google account is all you need. There is no separate sign-up.',
  'login.benefit':
    'A TV and a phone signed in to the same account stay connected — pick a product on the TV and this account’s phone hears about it right away.',
  'login.noClientId': 'This server has no Google client ID configured. Let an administrator know (',
  'login.alreadySignedIn': 'You are already signed in',
  'login.nativeButton': 'Sign in with Google',
  'login.nativeSigningIn': 'Signing in…',
  'login.nativeFailed': "Sign-in didn't finish. Please try again.",
  'login.shellTooOld': "This version of the app can't sign in. Please reinstall the app.",
  'login.callbackFailed': "Sign-in didn't finish. Please try again.",

  // ── Install guide ───────────────────────────────────────────────────
  'install.title': 'Add to your home screen to see the product',
  'install.text':
    'The product you picked on the TV is waiting. Add this to your home screen and next time it arrives as a notification.',
  'install.step1': 'Choose [Add to Home Screen] from the browser menu.',
  'install.step2': 'Open the app from the new icon.',
  'install.step3': 'The product you picked opens automatically.',
  'install.notRestoredTitle': 'Could not restore the product',
  'install.notRestoredText':
    'A QR code is only valid for a short while after it appears. Press [Buy on your phone] on the TV again to get a fresh one.',
  'install.checkNow': 'Check for the product now',
  'install.checking': 'Checking…',

  // ── Done screen ─────────────────────────────────────────────────────
  'done.title': 'Completion screen (demo)',
  'done.text':
    'The real payment happens at the store. This screen stands in for it, so a demo can show how far a hand-off started on the TV actually got.',
  'done.recorded': 'purchase_completed recorded',
  'done.home': 'Home',

  // ── Boot failure ────────────────────────────────────────────────────
  'boot.configFailed': 'Could not load the configuration',
  'boot.server': 'Server: {url}',
};
